/**
 * Owner: Masa Bayu
 * File: qxp-sheet-payload.ts
 * Date:Time: 2026-10-29 09:46
 * Description: Baca layout + linePaint + nombor halaman daripada muatan QXP
 *   (salinan QXW). Tiada invent: medan tiada → null.
 */

export type QxpSheetLayout = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly rotation: number;
  readonly zIndex: number;
};

export type QxpSheetSegment = {
  readonly text: string;
  readonly x: number;
  readonly width: number;
};

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Nombor halaman 1-based: `page.number` atau `el_pN_` dalam targetRef. */
export function pageNumberOfPayload(raw: {
  readonly targetRef?: unknown;
  readonly page?: unknown;
}): number | null {
  const page = raw.page;
  if (typeof page === 'object' && page !== null) {
    const number = finite((page as { number?: unknown }).number);
    if (number !== null && Number.isInteger(number) && number >= 1) {
      return number;
    }
  }
  const ref = typeof raw.targetRef === 'string' ? raw.targetRef : '';
  const match = /el_p(\d+)_/.exec(ref);
  if (match === null) {
    return null;
  }
  const parsed = Number.parseInt(match[1] ?? '', 10);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
}

export function layoutOfPayload(raw: { readonly layout?: unknown }): QxpSheetLayout | null {
  const layout = raw.layout;
  if (typeof layout !== 'object' || layout === null) {
    return null;
  }
  const box = layout as Record<string, unknown>;
  const x = finite(box.x);
  const y = finite(box.y);
  const width = finite(box.width);
  const height = finite(box.height);
  if (x === null || y === null || width === null || height === null) {
    return null;
  }
  return {
    x,
    y,
    width,
    height,
    rotation: finite(box.rotation) ?? 0,
    zIndex: finite(box.zIndex) ?? 0,
  };
}

export function segmentsOfPayload(raw: { readonly linePaint?: unknown }): readonly QxpSheetSegment[] | null {
  const paint = raw.linePaint;
  if (typeof paint !== 'object' || paint === null) {
    return null;
  }
  const shape = paint as Record<string, unknown>;
  if (shape.extension !== 'R11-LINE-02' || !Array.isArray(shape.segments)) {
    return null;
  }
  const segments: QxpSheetSegment[] = [];
  for (const entry of shape.segments) {
    if (typeof entry !== 'object' || entry === null) {
      return null;
    }
    const seg = entry as Record<string, unknown>;
    const x = finite(seg.x);
    const width = finite(seg.width);
    if (typeof seg.text !== 'string' || x === null || width === null) {
      return null;
    }
    segments.push({ text: seg.text, x, width });
  }
  return segments.length > 0 ? segments : null;
}
